/**
 * @author       sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright    Copyright (c) 2023, Zumper
 * @description  LinkPackLazyLoadableSection component
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { createLazyLoadableSection } from '@zcode/lazy-loadable-section'
import { ErrorFallback, LoadingFallback } from '@zcode/nr/es/shared/components/Fallback'
import type { ErrorBoundaryProps } from 'react-error-boundary'

import { logger } from '../../../logger'

const fallbackStyle = css`
  height: 396px;
`

const onRenderError: ErrorBoundaryProps['onError'] = (error, info) => {
  logger.error('render error for LinkPackLazyLoadableSection', info, error)
}

const onLoadingError = (error: unknown) => {
  logger.error('loading error for LinkPackLazyLoadableSection', error)
}

const loadFn = /* #__LOADABLE__ */ async () =>
  await import(/* webpackChunkName: "LinkPackWrapper" */ './LinkPackWrapper')

const message = `Sorry, we couldn't load the link pack...`

const handleReload = async () => await LinkPackLazyLoadableSection.reload()

const LinkPackErrorFallback = () => (
  <ErrorFallback css={fallbackStyle} message={message} onClickReload={handleReload} />
)
export const LinkPackLoadingFallback = () => (
  <LoadingFallback css={fallbackStyle} name="linkpack section" />
)

const options = {
  errorFallback: LinkPackErrorFallback,
  loadingFallback: LinkPackLoadingFallback,
  onRenderError,
  onLoadingError,
}

export const LinkPackLazyLoadableSection = createLazyLoadableSection(loadFn, options)
